import { useEffect } from 'react';
import { isRouteErrorResponse, useRouteError } from 'react-router';
import { AppLink } from '@/components/ui/AppLink';
import { Button } from '@/components/ui/Button';
import { buttonClasses } from '@/components/ui/button-classes';
import { Section } from '@/components/ui/Section';
import { useT } from '@/lib/i18n';
import { reportError } from '@/lib/monitoring';
import { Seo } from '@/lib/seo';

/** A lazy route chunk that failed to load — usually a new deploy replaced it, or the network dropped. */
const CHUNK_ERROR =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Unable to preload CSS/i;

const isChunkError = (error: unknown): boolean =>
  error instanceof Error && CHUNK_ERROR.test(error.message);

/**
 * Route `errorElement`: 404-style responses, render/loader errors and failed lazy chunks all land
 * here with a clear message and a way out (reload / back home). Nested inside the shell it keeps the
 * header and footer; `standalone` (root-level errors) brings its own `<main>`.
 */
export function RouteError({ standalone = false }: { standalone?: boolean }) {
  const t = useT();
  const error = useRouteError();

  const notFound = isRouteErrorResponse(error) && error.status === 404;

  useEffect(() => {
    console.error(error);
    // A mistyped address is not a fault of ours; everything else (render and loader errors, a chunk that
    // failed to load after a deploy) is reported once, without personal data (`lib/monitoring`).
    if (!notFound) {
      reportError(
        isRouteErrorResponse(error)
          ? new Error(`Route error response ${String(error.status)}`)
          : error,
        { source: 'router' },
      );
    }
  }, [error, notFound]);
  const title = notFound ? t('errors.notFound.title') : t('errors.boundary.title');
  const body = notFound
    ? t('errors.notFound.body')
    : isChunkError(error)
      ? t('errors.chunk')
      : t('errors.boundary.body');

  const content = (
    <>
      <Seo title={title} noindex path="/" />
      <Section headingLevel={1} title={title} divider={false}>
        <p className="max-w-[42rem] t-lead text-fg-muted">{body}</p>
        <div className="mt-8 flex flex-wrap gap-3">
          {notFound ? null : (
            <Button
              onClick={() => {
                window.location.reload();
              }}
            >
              {isChunkError(error) ? t('errors.reload') : t('common.retry')}
            </Button>
          )}
          <AppLink to="/" className={buttonClasses({ variant: notFound ? 'primary' : 'outline' })}>
            {t('common.backHome')}
          </AppLink>
        </div>
      </Section>
    </>
  );

  return standalone ? (
    <main id="main" tabIndex={-1} className="app-main">
      {content}
    </main>
  ) : (
    content
  );
}
